import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ExternalLink, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ROUTES } from "@/lib/routes";

const EMPTY_LANG = { title: "", excerpt: "", intro: "", sections: [] };
const EMPTY = { slug: "", published: true, cover: "", pl: { ...EMPTY_LANG }, en: { ...EMPTY_LANG } };
const btn = "inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors";

function Field({ label, children }) {
  return <label className="block"><span className="text-xs font-semibold text-muted-foreground mb-1.5 block">{label}</span>{children}</label>;
}

function LangEditor({ lg, data, onChange, aid }) {
  const set = (patch) => onChange({ ...data, ...patch });
  const setSection = (i, patch) => set({ sections: data.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const move = (i, d) => { const s = [...data.sections]; const [x] = s.splice(i, 1); s.splice(i + d, 0, x); set({ sections: s }); };
  const tid = (k) => `article-${aid}-${lg}-${k}`;
  return (
    <div className="space-y-4">
      <Field label={`Tytuł (${lg.toUpperCase()})${lg === "pl" ? " *" : ""}`}><Input data-testid={tid("title")} value={data.title} onChange={(e) => set({ title: e.target.value })} className="bg-background" /></Field>
      <Field label="Zajawka (meta description, do 400 znaków)"><Textarea data-testid={tid("excerpt")} rows={2} value={data.excerpt} onChange={(e) => set({ excerpt: e.target.value })} className="bg-background text-sm" /></Field>
      <Field label="Wstęp"><Textarea data-testid={tid("intro")} rows={4} value={data.intro} onChange={(e) => set({ intro: e.target.value })} className="bg-background text-sm" /></Field>
      <div className="space-y-3">
        {data.sections.map((s, i) => (
          <div key={i} data-testid={tid(`section-${i + 1}`)} className="rounded-xl border border-border p-3 space-y-2 bg-muted/30">
            <div className="flex items-center gap-2">
              <Input data-testid={tid(`section-heading-${i + 1}`)} placeholder="Nagłówek sekcji (H2)" value={s.heading} onChange={(e) => setSection(i, { heading: e.target.value })} className="bg-background text-sm font-semibold" />
              <button type="button" aria-label="W górę" disabled={i === 0} onClick={() => move(i, -1)} className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30"><ArrowUp size={14} /></button>
              <button type="button" aria-label="W dół" disabled={i === data.sections.length - 1} onClick={() => move(i, 1)} className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30"><ArrowDown size={14} /></button>
              <button type="button" aria-label="Usuń" data-testid={tid(`section-delete-${i + 1}`)} onClick={() => set({ sections: data.sections.filter((_, j) => j !== i) })} className="p-1.5 rounded-full text-brand-red hover:bg-brand-red/10"><Trash2 size={14} /></button>
            </div>
            <Textarea data-testid={tid(`section-body-${i + 1}`)} rows={5} placeholder="Treść sekcji… (akapity oddzielaj pustą linią, listy numerowane 1. 2. 3., linki: [tekst](adres) lub <a href=adres>tekst</a>; przycisk: [[PRZYCISK]] = „Wybierz pakiet” → kreator, albo [[PRZYCISK: Tekst | adres]]; link do kreatora: /biuro-wirtualne-warszawa/zamow-pakiet/smart?okres=24)" value={s.body} onChange={(e) => setSection(i, { body: e.target.value })} className="bg-background text-sm" />
          </div>
        ))}
        <button type="button" data-testid={tid("add-section")} disabled={data.sections.length >= 40} onClick={() => set({ sections: [...data.sections, { heading: "", body: "" }] })} className={`${btn} border border-dashed border-border hover:bg-muted w-full justify-center py-3`}><Plus size={13} /> Dodaj sekcję</button>
      </div>
    </div>
  );
}

function ArticleEditor({ article, onSaved, onDeleted }) {
  const [form, setForm] = useState(article);
  const [lg, setLg] = useState("pl");
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(article), [article]);
  const isNew = !article.id;
  const aid = article.id || "new";

  const save = async () => {
    setBusy(true);
    try {
      const r = isNew ? await api.post("/admin/articles", form) : await api.put(`/admin/articles/${article.id}`, form);
      toast.success(isNew ? "Artykuł dodany." : "Artykuł zapisany.");
      onSaved(r.data, isNew);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się zapisać.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm("Usunąć artykuł? Tej operacji nie można cofnąć.")) return;
    await api.delete(`/admin/articles/${article.id}`);
    toast.success("Artykuł usunięty.");
    onDeleted(article.id);
  };

  return (
    <div data-testid={`article-editor-${aid}`} className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h3 className="font-display font-bold text-base">{isNew ? "Nowy artykuł" : form.pl?.title || "Artykuł"}</h3>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs font-semibold"><Switch data-testid={`article-${aid}-published`} checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /> {form.published ? "Opublikowany" : "Szkic"}</label>
          {!isNew && <a data-testid={`article-${aid}-open`} href={ROUTES.article(form.slug)} target="_blank" rel="noopener noreferrer" className={`${btn} border border-border hover:bg-muted`}><ExternalLink size={13} /> Otwórz</a>}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Adres (slug) — puste = z tytułu"><Input data-testid={`article-${aid}-slug`} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="jak-zalozyc-spolke" className="bg-background font-mono text-xs" /></Field>
        <Field label="Grafika (URL, opcjonalnie)"><Input data-testid={`article-${aid}-cover`} value={form.cover || ""} onChange={(e) => setForm({ ...form, cover: e.target.value })} placeholder="/assets/wirtualne-biuro-warszawa-mokotow-1.jpg" className="bg-background font-mono text-xs" /></Field>
      </div>
      <p className="text-[11px] text-muted-foreground font-mono">Adres: {ROUTES.article(form.slug || "…")}</p>
      <div className="inline-flex rounded-full bg-muted p-1 text-xs font-semibold">
        {["pl", "en"].map((l) => <button key={l} type="button" data-testid={`article-${aid}-lang-${l}`} onClick={() => setLg(l)} className={`px-4 py-1.5 rounded-full ${lg === l ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l.toUpperCase()}</button>)}
      </div>
      <LangEditor lg={lg} aid={aid} data={form[lg]} onChange={(d) => setForm({ ...form, [lg]: d })} />
      <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-border">
        {!isNew && <button type="button" data-testid={`article-${aid}-delete`} onClick={remove} className={`${btn} text-brand-red hover:bg-brand-red/10`}><Trash2 size={13} /> Usuń</button>}
        <button type="button" data-testid={`article-${aid}-save`} onClick={save} disabled={busy} className={`${btn} bg-brand-navy text-white hover:bg-brand-teal disabled:opacity-50`}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Zapisz</button>
      </div>
    </div>
  );
}

export function ArticlesAdmin() {
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/admin/articles").then((r) => setList(r.data.articles)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (!list) return <div className="p-8 text-center text-muted-foreground"><Loader2 className="animate-spin inline" /></div>;
  return (
    <div data-testid="articles-admin" className="space-y-6">
      <div className="bg-card border border-border rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-lg">Baza wiedzy — artykuły</h2>
          <p className="text-xs text-muted-foreground mt-1">Każdy artykuł ma własny adres <span className="font-mono">{ROUTES.blog}/…</span>, tytuł SEO, opis i dane strukturalne. Trzy pierwsze publikowane artykuły pokazują się na stronie głównej w sekcji Wiedza.</p>
        </div>
        <button type="button" data-testid="articles-add" onClick={() => setAdding(true)} disabled={adding} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark disabled:opacity-50`}><Plus size={13} /> Nowy artykuł</button>
      </div>
      {adding && <ArticleEditor article={EMPTY} onSaved={() => { setAdding(false); load(); }} onDeleted={() => setAdding(false)} />}
      {list.map((a) => <ArticleEditor key={a.id} article={a} onSaved={load} onDeleted={() => load()} />)}
    </div>
  );
}
